import React, { useEffect, useMemo, useState } from "react";
import useResponsiveFlags from "../../hooks/useResponsiveFlags";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import axios from "../../api/axios";
import FilterMenuOrg from "./FilterMenuOrg";
import OrgHeadFiles from "./OrgTableHead";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Alert,
} from "@mui/material";
import WrapperContainer from "../../components/utilities/WrapperContainer";

const OrgTable = () => {
  const { isDesktop, isSmallMobileScreen } = useResponsiveFlags();
  const [data, setData] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [search, setSearch] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    if (!search) handleSearchButtonClick();
  }, [search]);

  const fetchData = async () => {
    setDataLoading(true);
    setErrorMessage("");
    const params = {
      page: page + 1,
      per_page: rowsPerPage,
    };
    if (order) params.sort_order = order;
    if (orderBy) params.sort_by = orderBy;
    if (filters.startDate) params.date_from = filters.startDate;
    if (filters.endDate) params.date_to = filters.endDate;
    if (search) params.search = search;
    axios
      .get("/api/admin_dashboard/orgsdata", { params })
      .then((response) => {
        const result = response.data;
        setData(result.data || []);
        setTotalCount(result.total || 0);
        setDataLoading(false);
      })
      .catch((err) => {
        setErrorMessage("Failed to fetch orgs table data.");
        setDataLoading(false);
      });
  };

  const fetchHandleAction = (action) => {
    setErrorMessage("");
    setSuccessMessage("");
    const orgId = selectedRow;
    axios
      .post("/api/admin_dashboard/actionorgs", {
        action: action,
        org: orgId,
      })
      .then((response) => {
        if (response.data.success) {
          setSuccessMessage(response.data.message);
          fetchData();
        } else {
          setErrorMessage(response.data.message);
        }
      })
      .catch((err) => {
        if (action === "toggle")
          setErrorMessage(
            `Failed to toggle provisional status of ${
              data.find((o) => o.id === orgId).name
            } organization due to internal error`
          );
        if (action === "delete")
          setErrorMessage(
            `Failed to delete ${
              data.find((o) => o.id === orgId).name
            } organization due to internal error`
          );
      });
  };
  
  const handleToggleProvisional = async () => {
    openConfirmationDialog(
      "Confirm Toggle Provisional Status",
      `Are you sure you want to toggle the provisional status of organization - ${
        data.find((o) => o.id === selectedRow).name
      } ?`,
      async () => {
        closeConfirmationDialog();
        fetchHandleAction("toggle");
        setSelectedRow("");
      }
    );
  };

  const handleDeleteOrg = async () => {
    openConfirmationDialog(
      "Confirm Delete Organization",
      `Are you sure you want to delete organization ${
        data.find((o) => o.id === selectedRow).name
      } ? This action cannot be undone.`,
      async () => {
        closeConfirmationDialog();
        fetchHandleAction("delete");
        setSelectedRow("");
      }
    );
  };
  
  const handleSelectRow = (orgId) => {
    setSelectedRow((prev) => (prev === orgId ? null : orgId));
  };

  const openConfirmationDialog = (title, message, onConfirm) => {
    setConfirmDialog({ open: true, title, message, onConfirm });
  };

  const closeConfirmationDialog = () => {
    setConfirmDialog({ ...confirmDialog, open: false });
  };

  const handleFilterChange = (column, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [column]: prevFilters[column] === value ? null : value,
    }));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value.toLowerCase());
  };

  const handleSearchButtonClick = () => {
    if (page != 0) setPage(0);
    else fetchData();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (property) => {
    const isAscending = orderBy === property && order === "asc";
    setOrder(isAscending ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = useMemo(() => {
    return [...data].sort(getComparator(order, orderBy));
  }, [data, order, orderBy]);

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function descendingComparator(a, b, orderBy) {
    let valueA = a[orderBy];
    let valueB = b[orderBy];

    if (orderBy === "regdate") {
      valueA = valueA ? new Date(valueA) : null;
      valueB = valueB ? new Date(valueB) : null;
    }

    if (orderBy === "id" || orderBy === "runs_count") {
      valueA = Number(valueA);
      valueB = Number(valueB);
    }

    if (typeof valueA === "string") valueA = valueA.toLowerCase();
    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA == null) return 1;
    if (valueB == null) return -1;

    if (valueB < valueA) return -1;
    if (valueB > valueA) return 1;
    return 0;
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const dayName = days[date.getDay()];
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = String(date.getFullYear()).slice(-2);

    return `${dayName} ${month}/${day}/${year}`;
  };

  return (
    <>
      <WrapperContainer
        title="Organizations"
        loggedin={true}
        content={
          <>
            <div
              style={{
                backgroundColor: "#ddd",
                padding: "8px 8px 0px 8px",
                borderRadius: "5px",
                marginBottom: "8px",
              }}
            >
              <p className="mb-1">
                Adjust Organistion :{" "}
                {selectedRow
                  ? `Orgnisation ${
                      data.find((o) => o.id === selectedRow).name
                    } has been selected.`
                  : "Please select an organization below to make changes"}
              </p>
              <div
                style={{
                  display: "inline-block",
                  cursor: selectedRow ? "pointer" : "not-allowed",
                  paddingBottom: "8px",
                }}
              >
                <button
                  className="btn btn-primary me-3"
                  onClick={handleToggleProvisional}
                  disabled={!selectedRow}
                  style={{
                    color: "#fff",
                    border: "none",
                  }}
                >
                  Toggle Provisional
                </button>
              </div>
              <div
                style={{
                  display: "inline-block",
                  cursor:
                    selectedRow &&
                    data.find((org) => org.id === selectedRow)?.provisional &&
                    !data.find((org) => org.id === selectedRow)?.user_count
                      ? "pointer"
                      : "not-allowed",
                  paddingBottom: "8px",
                }}
              >
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteOrg}
                  disabled={
                    !(
                      selectedRow &&
                      data.find((org) => org.id === selectedRow)?.provisional &&
                      !data.find((org) => org.id === selectedRow)?.user_count
                    )
                  }
                  style={{
                    color: "#fff",
                    border: "none",
                  }}
                >
                  Delete Organization
                </button>
              </div>
            </div>
            <Dialog open={confirmDialog.open} onClose={closeConfirmationDialog}>
              <DialogTitle>{confirmDialog.title}</DialogTitle>
              <DialogContent>{confirmDialog.message}</DialogContent>
              <DialogActions>
                <Button onClick={closeConfirmationDialog}>Cancel</Button>
                <Button
                  onClick={() =>
                    confirmDialog.onConfirm && confirmDialog.onConfirm()
                  }
                  color={
                    confirmDialog.title.toLowerCase().includes("delete")
                      ? "error"
                      : "primary"
                  }
                >
                  Confirm
                </Button>
              </DialogActions>
            </Dialog>

            <div className="d-flex flex-column-reverse flex-xl-row justify-content-between mb-xl-3">
              <div className="d-flex align-items-center">
                <input
                  type="text"
                  className="form-control search-icon"
                  placeholder="Search"
                  value={search}
                  onChange={handleSearchChange}
                />
                <Button
                  size="medium"
                  variant="contained"
                  onClick={handleSearchButtonClick}
                  sx={{ padding: "6px 0", marginLeft: "10px" }}
                >
                  Search
                </Button>

                <FilterMenuOrg
                  handleFilterChange={handleFilterChange}
                  filters={filters}
                />
              </div>
            </div>
            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage("")}
                sx={{ mt:1 }}
              >
                {errorMessage}
              </Alert>
            )}

            {successMessage && (
              <Alert
                severity="success"
                onClose={() => setSuccessMessage("")}
                sx={{ mt:1 }}
              >
                {successMessage}
              </Alert>
            )}

            <TableContainer
              className="table-scroll-visible"
              sx={{
                height: isDesktop
                  ? "calc(100vh - 315px)"
                  : "calc(100vh - 400px)",
                minHeight: isDesktop ? "500px" : "300px",
                overflowX: "auto",
                overflowY: "auto",
              }}
            >
              <Table stickyHeader sx={{ minWidth: 750 }} aria-label="org table">
                <OrgHeadFiles
                  order={order}
                  orderBy={orderBy}
                  onSort={handleSort}
                />
                <TableBody>
                  {dataLoading ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <TableRow
                        key={row.id}
                        style={{
                          backgroundColor: row.provisional ? "#aaacb5" : "#fff",
                        }}
                      >
                        <TableCell
                          style={
                            !isSmallMobileScreen
                              ? {
                                  position: "sticky",
                                  top: 0,
                                  left: 0,
                                  zIndex: 10,
                                  backgroundColor: row.provisional
                                    ? "#aaacb5"
                                    : "#fff",
                                }
                              : {}
                          }
                        >
                          <input
                            type="checkbox"
                            checked={selectedRow === row.id}
                            onChange={() => handleSelectRow(row.id)}
                          />
                        </TableCell>
                        <TableCell
                          style={
                            !isSmallMobileScreen
                              ? {
                                  position: "sticky",
                                  top: 0,
                                  left: 45,
                                  zIndex: 10,
                                  backgroundColor: row.provisional
                                    ? "#aaacb5"
                                    : "#fff",
                                }
                              : {}
                          }
                          className="custom-size"
                        >
                          {row.id}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.name || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.address || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.city || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.state || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.zip || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.country || "N/A"}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.user_count || 0}
                        </TableCell>
                        <TableCell className="custom-size">
                          {formatDate(row.reg_date)}
                        </TableCell>

                        <TableCell className="custom-size">
                          {row.provisional}
                        </TableCell>
                        <TableCell className="custom-size">
                          {row.orgcode}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={13} align="center">
                        {search
                          ? "No matching organizations found."
                          : "No organizations found"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {!dataLoading && totalCount > 0 && (
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Rows per page:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} of ${count} total orgsanizations`
                }
                sx={{
                  "& .MuiTablePagination-toolbar": {
                    alignItems: "center",
                  },
                  "& .MuiTablePagination-selectLabel": {
                    margin: 0,
                  },
                  "& .MuiTablePagination-displayedRows": {
                    margin: 0,
                  },
                }}
              />
            )}
          </>
        }
      />
    </>
  );
};

export default OrgTable;
